import {
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	json,
	redirect,
} from "@remix-run/node"
import {
	type ClientLoaderFunctionArgs,
	Form,
	useLoaderData,
	useNavigation,
} from "@remix-run/react"
import { and, eq, sql } from "drizzle-orm"
import { chip } from "drizzle/chip.sql"
import { location } from "drizzle/location.sql"
import { user } from "drizzle/user.sql"
import { P, match } from "ts-pattern"
import { Button } from "~/components/Button"
import { Input } from "~/components/Input"
import { ENABLE_FRIEND_CONNECTIONS, EVENT_DESCRIPTION, EVENT_NAME } from "~/constants"
import { generateUniqueId } from "~/lib/utils"
import { auth } from "~/services/auth.server"
import { getChipIdFromIykRef } from "./validate-iyk-ref"

export async function loader({ request, context }: LoaderFunctionArgs) {
	const { authenticator, getSession, commitSession } = await auth(context)
	const userSession = await authenticator.isAuthenticated(request)
	const session = await getSession(request.headers.get("Cookie"))
	const authError = session.get("auth:error")

	const url = new URL(request.url)
	const iykRef = url.searchParams.get("iykRef")
	const iykChip = await getChipIdFromIykRef(iykRef)

	return await match({ iykRef, iykChip, userSession })
		// SCENARIO 1: Fresh visitor with no IYK reference, no chip data, and not logged in
		// Result: Show landing page with login options
		.with({ iykRef: null, iykChip: null, userSession: null }, async () => {
			// Only generate fakeChipId if ENABLE_FRIEND_CONNECTIONS is false
			const chipId = ENABLE_FRIEND_CONNECTIONS ? undefined : generateUniqueId(50)
			return json({ chipId, authError } as const, {
				headers: {
					"Set-Cookie": await commitSession(session),
				},
			})
		})
		// SCENARIO 2: User is logged in but no chip was scanned (direct URL access)
		// Result: If user exists in DB, redirect to home page
		.with({ iykChip: null, userSession: P.not(P.nullish) }, async (input) => {
			console.log("no iykChip, userSession")
			const [userExists] = await context.db
				.select({ exists: sql<number>`1` })
				.from(user)
				.where(eq(user.userId, input.userSession.userId))
				.limit(1)

			if (userExists)
				throw redirect("/home", {
					headers: {
						"Set-Cookie": await commitSession(session),
					},
				})
		})
		// SCENARIO 3: Invalid/malformed chip was scanned
		// Result: Return 400 error
		.with({ iykChip: { isValid: false } }, async (input) => {
			console.log("invalid iykChip")
			throw new Response("Invalid IYK ref", { status: 400 })
		})
		// SCENARIO 4: Valid chip scanned but user is not logged in
		// Result: Show login page with chip data for registration
		.with(
			{ iykChip: { isValid: true }, userSession: P.nullish },
			async (input) => {
				console.log("iykChip, no userSession")
				return json({ chipId: input.iykChip.chipId, authError } as const, {
					headers: {
						"Set-Cookie": await commitSession(session),
					},
				})
			},
		)
		// SCENARIO 5: Valid chip scanned AND user is logged in
		// Result: Determine what type of chip it is and redirect accordingly
		.with(
			{
				iykChip: { isValid: true },
				userSession: P.not(P.nullish),
				iykRef: P.string,
			},
			async (input) => {
				console.log("iykChip, userSession")
				console.log({ input })
				const [chipRedirect] = await context.db
					.select({ userId: user.userId, locationId: location.locationId })
					.from(chip)
					.leftJoin(
						user,
						and(eq(chip.chipType, "person"), eq(chip.chipId, user.chipId)),
					)
					.leftJoin(
						location,
						and(
							eq(chip.chipType, "location"),
							eq(chip.chipId, location.chipId),
						),
					)
					.where(eq(chip.iykId, input.iykChip.chipId))
				console.log({ chipRedirect })

				return match(chipRedirect)
					// SUB-SCENARIO 5a: Chip exists but has no associated user or location
					// Result: Show error - chip not properly registered
					.with({ userId: P.nullish, locationId: P.nullish }, async () => {
						console.log(
							"the tapper is logged in, but this chip has no userId or locationId",
						)
						throw new Response(
							"The card is not registered yet. Please ask the owner to register their card by tapping it.",
						)
					})
					// SUB-SCENARIO 5b: User scanned their own chip
					// Result: Redirect to home page
					.with(
						{ userId: input.userSession.userId },
						async (chipRedirectInput) => {
							console.log("userId matches userSession userId")
							throw redirect("/home", {
								headers: {
									"Set-Cookie": await commitSession(session),
								},
							})
						},
					)
					// SUB-SCENARIO 5c: User scanned another person's chip
					// Result: Redirect to that user's profile page
					.with({ userId: P.string }, async (chipRedirectInput) => {
						console.log("has userId")
						session.flash("chip:userId", chipRedirectInput.userId)
						throw redirect(`/user/${chipRedirectInput.userId}`, {
							headers: {
								"Set-Cookie": await commitSession(session),
							},
						})
					})
					// SUB-SCENARIO 5d: User scanned a location chip
					// Result: Redirect to location page for quest/activity completion
					.with({ locationId: P.string }, async (chipRedirectInput) => {
						console.log("has locationId")
						session.flash("chip:locationId", chipRedirectInput.locationId)
						throw redirect(`/location/${input.iykRef}`, {
							headers: {
								"Set-Cookie": await commitSession(session),
							},
						})
					})
					// SUB-SCENARIO 5e: Chip not found in database
					// Result: Show error - unregistered chip
					.with(P.nullish, async () => {
						console.log(
							"the tapper is logged in, but this chip is not in our database",
						)
						throw new Response(
							"This card is not registered. \n\nPlease ask the owner to register by tapping.",
						)
					})
					.exhaustive()
			},
		)
		// SCENARIO 6: Has IYK reference but no chip data and not logged in
		// Result: Show landing page (similar to scenario 1)
		.with(
			{ iykRef: P.string, iykChip: P.nullish, userSession: P.nullish },
			async () => {
				console.log("no iykChip, no userSession")
				return json({ chipId: null, authError } as const, {
					headers: {
						"Set-Cookie": await commitSession(session),
					},
				})
			},
		)
		// FALLBACK: Any other combination not covered above
		// Result: Return 400 error for invalid IYK reference
		.otherwise(async () => {
			console.log("invalid iykRef")
			throw new Response("Invalid IYK ref", { status: 400 })
		})
}

export async function clientLoader({
	request,
	serverLoader,
}: ClientLoaderFunctionArgs) {
	const serverData = await serverLoader<typeof loader>()
	console.log({ serverData })
	if (serverData.chipId) {
		localStorage.setItem("chipId", serverData.chipId)
	}

	return serverData
}
clientLoader.hydrate = true

export async function action({ request, context }: ActionFunctionArgs) {
	// const formData = await request.formData()
	// const chipId = formData.get("chipId")
	// if (!chipId) {
	// 	throw new Response("No chip id", { status: 400 }) // ! TODO find a better way to handle this
	// }

	const { authenticator, commitSession, getSession } = await auth(context)
	await authenticator.authenticate("TOTP", request, {
		successRedirect: "/verify",
		failureRedirect: new URL(request.url).pathname,
		throwOnError: true,
	})
	return null
}

export default function Index() {
	const { chipId, authError } = useLoaderData<typeof loader>()
	const navigation = useNavigation()

	return (
		<div className="grid h-full grid-rows-[1fr_auto] px-4 pb-7">
			<div className="flex flex-col justify-center gap-6">
				<div>
					<h1 className="text-2xl">Welcome to {EVENT_NAME}</h1>
					<p className="text-foreground/60 text-xs">{EVENT_DESCRIPTION}</p>
				</div>

				<Form method="POST" id="email-form">
					<input
						type="hidden"
						name="chipId"
						defaultValue={chipId ?? undefined}
					/>
					<div className="flex flex-col gap-1">
						<label htmlFor="email" className="text-xs">
							Email
						</label>
						<Input
							type="email"
							name="email"
							id="email"
							placeholder="Your email"
						/>
						<div className="min-h-4">
							{authError ? (
								<p className="text-error text-xs">{authError.message}</p>
							) : null}
						</div>
					</div>
				</Form>
			</div>

			<div className="flex flex-col gap-6">
				<Button
					type="submit"
					form="email-form"
					isLoading={navigation.state === "submitting"}
				>
					Continue
				</Button>
			</div>
		</div>
	)
}
